import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GitMerge, Search, ChevronDown, ChevronRight, Layers, Archive, RotateCcw } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { refreshCatalogs } from "../components/MoneyMovementFields";
import FamilySelect, { invalidateFamilyCache } from "../components/FamilySelect";
import GroupingSelect from "../components/GroupingSelect";
import CatalogItemsSection from "../components/CatalogItemsSection";
import AssetsCatalogSection from "../components/AssetsCatalogSection";
import GroupingsTree from "../components/GroupingsTree";

const UNGROUPED = "Sin agrupar";

// ---- helpers ----
function groupItems({ items, mode, extraKind, familiesById }) {
  // Devuelve { groupName: [items] } y una lista ordenada de grupos
  const map = {};
  if (mode === "parent") {
    // Categorías: los que tienen parent_id se meten bajo su padre; los root sin hijos van a "Sin agrupar"
    const byId = Object.fromEntries(items.map((c) => [c.id, c]));
    const childrenOf = {};
    items.forEach((c) => {
      if (c.parent_id) {
        childrenOf[c.parent_id] = childrenOf[c.parent_id] || [];
        childrenOf[c.parent_id].push(c);
      }
    });
    items.forEach((c) => {
      if (c.parent_id && byId[c.parent_id]) return; // ya va bajo su padre
      if (childrenOf[c.id]) {
        // Es un padre: crea grupo con su nombre
        map[c.name] = map[c.name] || [];
        // El padre no se muestra como item repetido, solo sus hijos
        childrenOf[c.id].forEach((ch) => map[c.name].push(ch));
      } else {
        // Root sin hijos → Sin agrupar
        map[UNGROUPED] = map[UNGROUPED] || [];
        map[UNGROUPED].push(c);
      }
    });
  } else {
    // Catálogos planos: agrupar por family_id_ref → nombre resuelto (o "Sin familia")
    items.forEach((it) => {
      let key = UNGROUPED;
      if (it.family_id_ref && familiesById && familiesById[it.family_id_ref]) {
        key = familiesById[it.family_id_ref];
      } else if (it.group && it.group.trim()) {
        // fallback registros legacy con solo `group` texto libre pendiente de migrar
        key = `${it.group.trim()} · legacy`;
      }
      map[key] = map[key] || [];
      map[key].push(it);
    });
  }
  const keys = Object.keys(map).sort((a, b) => {
    if (a === UNGROUPED) return 1;
    if (b === UNGROUPED) return -1;
    return a.localeCompare(b, "es");
  });
  keys.forEach((k) => map[k].sort((a, b) => a.name.localeCompare(b.name, "es")));
  return { map, keys };
}

function CrudSection({ endpoint, mergeKey, title, desc, allowMerge, testPrefix, listUrl, extraParams, groupMode, categoryKind, familyKind }) {
  // groupMode: "parent" (para categorías) | "field" (para catálogos con campo family_id_ref)
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const [newFamilyId, setNewFamilyId] = useState(null);   // V1.42 modo field
  const [newParentId, setNewParentId] = useState(""); // solo modo parent (para nuevas hijas)
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [merge, setMerge] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [families, setFamilies] = useState([]);            // catálogo de catalog_families del kind actual

  const storageKey = `lv_expanded_${testPrefix}`;
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) setExpanded(JSON.parse(saved));
    } catch {}
  }, [storageKey]);
  const toggleGroup = (k) => {
    setExpanded((prev) => {
      const currentOpen = prev[k] !== false; // default open
      const next = { ...prev, [k]: !currentOpen };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const listEndpoint = listUrl || endpoint;
  const loadItems = () => api.get(listEndpoint, extraParams ? { params: extraParams } : undefined).then((r) => setItems(r.data));
  const loadFamilies = () => {
    if (!familyKind) return Promise.resolve();
    // V1.44.2: fuente única — las agrupaciones viven en /categories (kind X_group), igual que FamilySelect y el área Agrupaciones
    return api.get("/categories", { params: { kind: `${familyKind}_group` } }).then((r) => setFamilies(r.data || []));
  };
  useEffect(() => { loadItems(); loadFamilies(); }, [listEndpoint, JSON.stringify(extraParams || {}), familyKind]);
  const load = () => Promise.all([loadItems(), loadFamilies()]);
  const familiesById = useMemo(() => {
    const byId = Object.fromEntries((families || []).map((f) => [f.id, f]));
    const pathOf = (f) => {
      const parts = [f.name];
      const seen = new Set([f.id]);
      let p = f.parent_id;
      while (p && byId[p] && !seen.has(p)) { seen.add(p); parts.unshift(byId[p].name); p = byId[p].parent_id; }
      return parts.join(" › ");
    };
    return Object.fromEntries((families || []).map((f) => [f.id, pathOf(f)]));
  }, [families]);

  const create = async () => {
    try {
      const payload = { name, active: true };
      if (groupMode === "field" && newFamilyId) payload.family_id_ref = newFamilyId;
      if (groupMode === "parent") {
        payload.kind = categoryKind;
        if (newParentId && newParentId !== "__none__") payload.parent_id = newParentId;
      }
      await api.post(endpoint, payload);
      refreshCatalogs();
      toast.success("Creado");
      setName("");
      setNewFamilyId(null);
      setNewParentId("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    try {
      const payload = { name: editing.name, active: editing.active };
      if (groupMode === "field") payload.family_id_ref = editing.family_id_ref || null;
      if (groupMode === "parent") {
        payload.kind = categoryKind;
        payload.parent_id = (editing.parent_id && editing.parent_id !== "__none__") ? editing.parent_id : null;
      }
      await api.put(`${endpoint}/${editing.id}`, payload);
      refreshCatalogs();
      toast.success("Actualizado");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (it, v) => {
    try {
      const payload = { name: it.name, active: v };
      if (groupMode === "field") payload.family_id_ref = it.family_id_ref || null;
      if (groupMode === "parent") {
        payload.kind = categoryKind;
        payload.parent_id = it.parent_id || null;
      }
      await api.put(`${endpoint}/${it.id}`, payload);
      refreshCatalogs();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.name}"?`)) return;
    try {
      await api.delete(`${endpoint}/${it.id}`);
      refreshCatalogs();
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doMerge = async () => {
    try {
      const src = merge.source_ids.filter((s) => s !== merge.target_id);
      if (!merge.target_id || src.length === 0) { toast.error("Selecciona destino y al menos un origen"); return; }
      if (mergeKey) {
        const r = await api.post(`/catalog/${mergeKey}/merge`, { into_id: merge.target_id, from_ids: src });
        toast.success(`${r.data.affected_records || 0} referencias reasignadas`);
      } else {
        const r = await api.post(`${endpoint}/merge`, { target_id: merge.target_id, source_ids: src });
        toast.success(`Fusionados ${r.data.moved || 0} elementos`);
      }
      setMerge(null); refreshCatalogs(); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = useMemo(
    () => (items || []).filter((it) => !q || it.name.toLowerCase().includes(q.toLowerCase())),
    [items, q]
  );

  const { map: grouped, keys } = useMemo(
    () => (items ? groupItems({ items: filtered, mode: groupMode, extraKind: categoryKind, familiesById }) : { map: {}, keys: [] }),
    [filtered, groupMode, categoryKind, items, familiesById]
  );

  // Para categorías: lista de posibles padres (raíz del mismo kind)
  const potentialParents = useMemo(
    () => (items || []).filter((c) => !c.parent_id),
    [items]
  );

  return (
    <div className="border border-border rounded-lg p-5" data-testid={`${testPrefix}-section`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading font-bold text-lg">{title}</h3>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
        {allowMerge && (items || []).length > 1 && (
          <Button type="button" variant="outline" size="sm" data-testid={`${testPrefix}-merge-btn`} onClick={() => setMerge({ target_id: "", source_ids: [] })}>
            <GitMerge size={14} className="mr-1" /> Fusionar duplicados
          </Button>
        )}
      </div>

      {/* Formulario de creación */}
      <div className="flex flex-wrap gap-2 mt-4">
        <Input
          data-testid={`${testPrefix}-name-input`}
          className="flex-1 min-w-[160px]"
          placeholder="Nombre…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && name && create()}
        />
        {groupMode === "field" && familyKind && (
          <div className="w-56">
            <FamilySelect
              testId={`${testPrefix}-family-select`}
              kind={familyKind}
              value={newFamilyId}
              onChange={(id) => setNewFamilyId(id)}
              placeholder="Familia (opcional)"
            />
          </div>
        )}
        {groupMode === "parent" && potentialParents.length > 0 && (
          <Select value={newParentId || "__none__"} onValueChange={(v) => setNewParentId(v)}>
            <SelectTrigger data-testid={`${testPrefix}-parent-select`} className="w-48"><SelectValue placeholder="Sin grupo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sin grupo</SelectItem>
              {potentialParents.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button data-testid={`${testPrefix}-create-btn`} onClick={create} disabled={!name.trim()}><Plus size={15} className="mr-1" /> Crear</Button>
      </div>

      <div className="mt-3 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid={`${testPrefix}-search`} className="pl-9" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {/* Acordeón por grupo */}
      <div className="mt-4 border border-border rounded-lg divide-y divide-border overflow-hidden" data-testid={`${testPrefix}-accordion`}>
        {items === null ? (
          <div className="p-4 text-sm text-muted-foreground">Cargando…</div>
        ) : keys.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Sin resultados.</div>
        ) : (
          keys.map((gk) => {
            const isOpen = expanded[gk] !== false; // default abierto
            const list = grouped[gk];
            return (
              <div key={gk} data-testid={`${testPrefix}-group-${gk}`}>
                <button
                  data-testid={`${testPrefix}-group-toggle-${gk}`}
                  onClick={() => toggleGroup(gk)}
                  className="w-full flex items-center justify-between p-3 bg-secondary/40 hover:bg-secondary/70 transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown size={15} className="text-muted-foreground" /> : <ChevronRight size={15} className="text-muted-foreground" />}
                    <span className="font-semibold text-sm">{gk}</span>
                    <span className="text-xs text-muted-foreground">({list.length})</span>
                  </div>
                </button>
                {isOpen && (
                  <div className="divide-y divide-border">
                    {list.map((it) => (
                      <div key={it.id} className={`flex items-center justify-between py-2 pl-10 pr-3 ${!it.active ? "opacity-50" : ""}`} data-testid={`${testPrefix}-row-${it.id}`}>
                        <div className="min-w-0 flex items-center gap-2">
                          <div className="text-sm font-medium truncate">{it.name}</div>
                          {!it.active && <span className="text-[10px] font-bold uppercase tracking-wider bg-secondary px-1.5 py-0.5 rounded">Inactivo</span>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button data-testid={`${testPrefix}-edit-${it.id}`} onClick={() => setEditing({ ...it })} className="text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
                          <Switch data-testid={`${testPrefix}-toggle-${it.id}`} checked={it.active} onCheckedChange={(v) => toggleActive(it, v)} className="scale-90" />
                          <button data-testid={`${testPrefix}-delete-${it.id}`} onClick={() => remove(it)} className="text-muted-foreground hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Edición */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-xs" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Editar</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input data-testid={`${testPrefix}-edit-name`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus />
              {groupMode === "field" && familyKind && (
                <FamilySelect
                  testId={`${testPrefix}-edit-family`}
                  kind={familyKind}
                  value={editing.family_id_ref || null}
                  legacyGroupName={!editing.family_id_ref ? (editing.group || "") : ""}
                  onChange={(id) => setEditing({ ...editing, family_id_ref: id })}
                  placeholder="Familia (opcional)"
                />
              )}
              {groupMode === "parent" && (
                <Select value={editing.parent_id || "__none__"} onValueChange={(v) => setEditing({ ...editing, parent_id: v === "__none__" ? null : v })}>
                  <SelectTrigger data-testid={`${testPrefix}-edit-parent`}><SelectValue placeholder="Sin grupo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin grupo</SelectItem>
                    {potentialParents.filter((p) => p.id !== editing.id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm">Activo</span>
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
              </div>
              <Button data-testid={`${testPrefix}-save-btn`} className="w-full" onClick={save} disabled={!editing.name.trim()}>Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {allowMerge && (
        <Dialog open={!!merge} onOpenChange={(v) => !v && setMerge(null)}>
          <DialogContent className="max-w-sm" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-heading">Fusionar duplicados</DialogTitle></DialogHeader>
            {merge && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground">Destino (se conserva)</label>
                  <Select value={merge.target_id} onValueChange={(v) => setMerge({ ...merge, target_id: v })}>
                    <SelectTrigger data-testid={`${testPrefix}-merge-target`}><SelectValue placeholder="Elige el registro final" /></SelectTrigger>
                    <SelectContent>{(items || []).map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Origen (se eliminan y sus referencias pasan al destino)</label>
                  <div className="max-h-60 overflow-y-auto border border-border rounded-lg p-2 space-y-1 mt-1">
                    {(items || []).filter((it) => it.id !== merge.target_id).map((it) => (
                      <label key={it.id} className="flex items-center gap-2 text-sm py-1">
                        <Checkbox
                          data-testid={`${testPrefix}-merge-src-${it.id}`}
                          checked={merge.source_ids.includes(it.id)}
                          onCheckedChange={(v) => {
                            const set = new Set(merge.source_ids);
                            if (v) set.add(it.id); else set.delete(it.id);
                            setMerge({ ...merge, source_ids: [...set] });
                          }}
                        />
                        {it.name}
                      </label>
                    ))}
                  </div>
                </div>
                <Button data-testid={`${testPrefix}-merge-confirm`} className="w-full" onClick={doMerge} disabled={!merge.target_id || merge.source_ids.length === 0}>Fusionar</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const CATALOG_TABS = [
  { id: "gasto", label: "Gastos" },
  { id: "ingreso", label: "Ingresos" },
  { id: "activo", label: "Activos" },
  { id: "proveedores", label: "Proveedores" },
  { id: "metodos", label: "Métodos de pago" },
  { id: "tipos-activos", label: "Tipos de activos" },
  { id: "compromiso", label: "Compromisos" },
  { id: "obligacion", label: "Obligaciones" },
];

const GROUPING_TABS = [
  { id: "gasto", label: "Gastos", kind: "gasto" },
  { id: "ingreso", label: "Ingresos", kind: "ingreso" },
  { id: "activo", label: "Activos", kind: "activo" },
  { id: "proveedores", label: "Proveedores", kind: "provider_group" },
  { id: "metodos", label: "Métodos de pago", kind: "payment_method_group" },
  { id: "tipos-activos", label: "Tipos de activos", kind: "asset_type_group" },
  { id: "compromiso", label: "Compromisos", kind: "compromiso" },
  { id: "obligacion", label: "Obligaciones", kind: "obligacion" },
];

const ITEM_DESCS = {
  gasto: "Conceptos reales de gasto (Luz Vivienda, Seguro Hogar, Cuota gimnasio…). Se seleccionan al registrar cada gasto. La agrupación clasifica el concepto.",
  ingreso: "Conceptos reales de ingreso (Nómina Empresa, Alquiler piso, Dividendos…). Se seleccionan al registrar cada ingreso.",
  activo: "Elementos reales de patrimonio (Vivienda Principal, Bankinter, Nissan Leaf…). Catálogo de referencia para el módulo Activos.",
  compromiso: "Conceptos reales de compromisos (Hipoteca Variable, Cuota comunidad…). Se seleccionan al crear compromisos.",
  obligacion: "Conceptos reales de obligaciones y financiaciones (Préstamo Coche, Hipoteca vivienda…). Se seleccionan al crear financiaciones.",
};

export default function Mantenimiento() {
  const [area, setArea] = useState("catalogo");
  const [tab, setTab] = useState("gasto");
  const [gtab, setGtab] = useState("gasto");

  const tabs = area === "catalogo" ? CATALOG_TABS : GROUPING_TABS;
  const current = area === "catalogo" ? tab : gtab;
  const setCurrent = area === "catalogo" ? setTab : setGtab;

  return (
    <div className="space-y-6" data-testid="listado-valores-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Listado de Valores</h1>
        <p className="text-sm text-muted-foreground mt-1">
          <span className="font-semibold text-foreground">Catálogo Maestro</span>: elementos reales que usan los movimientos. <span className="font-semibold text-foreground">Agrupaciones</span>: jerarquía ilimitada para clasificarlos.
        </p>
      </div>

      <div className="inline-flex rounded-lg border border-border p-1 bg-secondary/40" data-testid="lv-area-switch">
        <button
          onClick={() => setArea("catalogo")}
          data-testid="lv-area-catalogo"
          className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${area === "catalogo" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        >Catálogo Maestro</button>
        <button
          onClick={() => setArea("agrupaciones")}
          data-testid="lv-area-agrupaciones"
          className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${area === "agrupaciones" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        >Agrupaciones</button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setCurrent(t.id)}
            data-testid={`lv-tab-${area}-${t.id}`}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
              current === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {area === "catalogo" && (
        <>
          {["gasto", "ingreso", "compromiso", "obligacion"].includes(tab) && (
            <CatalogItemsSection kind={tab} title={CATALOG_TABS.find((t) => t.id === tab)?.label} desc={ITEM_DESCS[tab]} testPrefix={`ci-${tab}`} />
          )}
          {tab === "activo" && <AssetsCatalogSection testPrefix="ca-activo" />}
          {tab === "proveedores" && (
            <CrudSection endpoint="/providers" mergeKey="providers" title="Proveedores" desc="Empresas y proveedores reales (Iberdrola, Mapfre, Movistar…). Asigna una agrupación desde el área Agrupaciones." allowMerge testPrefix="prov"
              groupMode="field" familyKind="provider" />
          )}
          {tab === "metodos" && (
            <CrudSection endpoint="/payment-methods" mergeKey="payment_methods" title="Métodos de pago" desc="Métodos reales (Tarjeta Fin de Mes, Bizum, Transferencia, Efectivo…). Se seleccionan al registrar movimientos." allowMerge testPrefix="pm"
              groupMode="field" familyKind="payment_method" />
          )}
          {tab === "tipos-activos" && (
            <CrudSection endpoint="/asset_types" mergeKey="asset_types" title="Tipos de Activos" desc="Catálogo maestro consumido por el módulo Activos (Bancos y Caja, Inmuebles, Vehículos…)." allowMerge testPrefix="at"
              groupMode="field" familyKind="asset_type" />
          )}
        </>
      )}

      {area === "agrupaciones" && (() => {
        const g = GROUPING_TABS.find((t) => t.id === gtab);
        return (
          <GroupingsTree
            key={g.kind}
            kind={g.kind}
            title={`Agrupaciones · ${g.label}`}
            desc="Clasifican elementos del Catálogo Maestro. Jerarquía padre-hijo sin límite de niveles. Los movimientos nunca apuntan a agrupaciones."
            testPrefix={`ag-${g.id}`}
          />
        );
      })()}
    </div>
  );
}
